import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants, {SchemaType} from 'constants/ModelTypeConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {FromServerDataTableColumn, FromServerDataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {LOOKUP_FIELD_FIELD_TYPE_EXP_COND} from 'notebook/tables/lookup-field/LookupFieldDefinitionParser';
import {FieldType, RequiredLinkTagSchema, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';

import {
  FilterSpecifier,
  InputSampleLookupSpecifier,
  LookupSpecifier,
  SourceSpecifier,
  StageRunSpecifierFieldType,
} from './LookupFieldDefinitionConstants';

// This constant limits how deep the field tree can go.
//
// - Please don't increase this limit by much. We generate the field tree without any pruning, so if you increase the limit,
//   you could worsen performance exponentially!
//
// - On the other hand, this limit should be perfectly safe to reduce. The limit is only enforced at configuration time, not runtime.
//   Even if the limit is reduced too far, existing LOOKUPFIELDs will not break.
const MAX_CONFIGURABLE_FIELD_DEPTH = 5;

// This constant caps the *total* number of nested fields we generate, across the
// whole tree, regardless of depth.
//
// The depth limit alone does not bound the total node count: a schema whose fields
// link (directly or transitively) back to schemas that themselves have many link
// fields expands combinatorially up to MAX_CONFIGURABLE_FIELD_DEPTH. A pathological
// schema can produce millions of nodes, which freezes the browser and overflows the
// downstream tree picker (SelectTree builds a plain object keyed by every node id,
// which throws "Too many properties to enumerate").
//
// No realistic schema comes close to this cap, so hitting it means the deepest
// nesting of a pathological schema is truncated -- the configuration UI stays usable
// and existing LOOKUPFIELDs are unaffected (the limit is only enforced at
// configuration time, not at runtime).
const MAX_CONFIGURABLE_FIELD_COUNT = 5000;

export enum DATA_TYPE {
  DATA_TABLE = 'DATA_TABLE',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SAMPLE_ID = 'SAMPLE_ID',
  INPUT_SAMPLE = 'INPUT_SAMPLE',
}

export interface FieldsChainField {
  schemaType: SchemaType;
  uniqueFieldId: string;
  parentFieldId: string | null;
  name: string;
  tagSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  fieldType: FieldType;
  requiredLink: {
    tagSchema: TagSchemaSummaryWithFieldsGraphQL | TagSchemaSummary | null;
  };
}

function getSchemaIdForFieldsChainField(fieldsChainField: FieldsChainField): string {
  switch (fieldsChainField.schemaType) {
    case ModelTypeConstants.TAG_SCHEMA:
      return fieldsChainField.tagSchema!.id;
    default:
      throw new Error(`Unexpected schema type ${fieldsChainField.schemaType}`);
  }
}

export function generateInputSampleLookupSpecifier({
  fieldType,
  schemaId,
  stageRunId,
  stageRunSampleId,
}: {
  fieldType: StageRunSpecifierFieldType;
  schemaId: string | null;
  stageRunId: string | null;
  stageRunSampleId: string | null;
}): InputSampleLookupSpecifier {
  return {
    fieldName: null,
    fieldType,
    schemaId: schemaId || null,
    schemaName: null,
    stageRunId,
    stageRunSampleId,
    filterSpecifiers: [],
    sourceSpecifier: null,
  };
}

export function generateLookupSpecifier({
  topLevelSchema,
  fieldsChain,
  dataType,
  selectedSchemaField,
  selectedSchemaFieldSchemaType,
  selectedLinkTagSchema,
  selectedLinkBatchSchema,
  sampleSchemaFieldType,
  dataTableConfig,
  dataTableColumn,
  filterSpecifiers,
  sourceLookupSpecifier,
}: {
  topLevelSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  fieldsChain: Array<FieldsChainField>;
  dataType: DATA_TYPE;
  selectedSchemaField: SchemaField | null;
  selectedSchemaFieldSchemaType: SchemaType | null;
  selectedLinkTagSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  selectedLinkBatchSchema: BatchSchema | null;
  sampleSchemaFieldType: StageRunSpecifierFieldType | null;
  dataTableConfig: FromServerDataTableConfig | null;
  dataTableColumn: FromServerDataTableColumn | null;
  filterSpecifiers: Array<FilterSpecifier>;
  sourceLookupSpecifier: LookupSpecifier | null;
}): LookupSpecifier | null {
  if (topLevelSchema == null) {
    return null;
  }

  let sourceSpecifier = sourceLookupSpecifier
    ? ({lookupSpecifier: sourceLookupSpecifier} as SourceSpecifier)
    : null;
  // Iterate up to the second element (1) to exclude the innermost field.
  for (let i = fieldsChain.length - 1; i >= 1; i--) {
    sourceSpecifier = {
      lookupSpecifier: {
        schemaName: null,
        schemaId: getSchemaIdForFieldsChainField(fieldsChain[i]),
        fieldName: fieldsChain[i].name,
        fieldType: null,
        sourceSpecifier,
      },
    };
  }
  if (fieldsChain.length > 0 && (dataType === DATA_TYPE.DATA_TABLE || dataType === DATA_TYPE.SCHEMA_FIELD)) {
    // The final lookup for data table and schema fields is specified outside the fields chain
    // so include the innermost field as a source specifier.
    sourceSpecifier = {
      lookupSpecifier: {
        schemaName: null,
        schemaId: getSchemaIdForFieldsChainField(fieldsChain[0]),
        fieldName: fieldsChain[0].name,
        fieldType: null,
        sourceSpecifier,
      },
    };
  }

  let schemaId;
  let fieldName;
  let fieldType;
  switch (dataType) {
    case DATA_TYPE.DATA_TABLE:
      if (dataTableConfig == null || dataTableColumn == null) {
        return null;
      }
      schemaId = dataTableConfig.assaySchema.id;
      fieldName = dataTableColumn.name;
      fieldType = null;
      break;

    case DATA_TYPE.SCHEMA_FIELD:
      if (selectedSchemaField == null || selectedSchemaFieldSchemaType == null) {
        return null;
      }
      if (selectedSchemaFieldSchemaType === ModelTypeConstants.BATCH_SCHEMA) {
        assert(selectedLinkBatchSchema != null, 'Batch schema is expected when schema type is BATCH');
        schemaId = selectedLinkBatchSchema.id;
      } else {
        assert(selectedLinkTagSchema != null, 'Tag schema is expected when schema type is ENTITY');
        schemaId = selectedLinkTagSchema.id;
      }
      fieldName = selectedSchemaField.name;
      fieldType = null;
      break;

    case DATA_TYPE.SAMPLE_ID:
      if (fieldsChain.length > 0) {
        sourceSpecifier = {
          lookupSpecifier: {
            schemaName: null,
            schemaId: getSchemaIdForFieldsChainField(fieldsChain[0]),
            fieldName: fieldsChain[0].name,
            fieldType: null,
            sourceSpecifier,
          },
        };
      }
      assert(sampleSchemaFieldType != null, 'Expected a field type for sample ID lookup');
      schemaId = null;
      fieldName = null;
      fieldType = sampleSchemaFieldType;
      break;

    default:
      throw new Error(`Unhandled data type: ${dataType}`);
  }

  return {
    schemaName: null,
    schemaId,
    fieldName,
    fieldType,
    filterSpecifiers,
    sourceSpecifier,
  };
}

export function generateExpCondLookupSpecifier({
  experimentalConditionName,
  stageRunId,
  stageRunSampleId,
}: {
  experimentalConditionName: string;
  stageRunId: string | null;
  stageRunSampleId: string | null;
}): InputSampleLookupSpecifier {
  return {
    fieldName: experimentalConditionName,
    fieldType: LOOKUP_FIELD_FIELD_TYPE_EXP_COND,
    schemaId: null,
    schemaName: null,
    stageRunId,
    stageRunSampleId,
    filterSpecifiers: [],
    sourceSpecifier: null,
  };
}

export function generateUniqueFieldId({
  parentFieldId,
  id,
}: {
  parentFieldId: string | null;
  id: string | number;
}): string {
  return parentFieldId == null ? `${id}` : `${parentFieldId}-${id}`;
}

export function isFieldEntitySchemaLink(field: FieldsChainField | SchemaField): boolean {
  return (
    field.fieldType === FieldType.BIOENTITY_LINK &&
    field.requiredLink != null &&
    'tagSchema' in field.requiredLink &&
    field.requiredLink.tagSchema != null
  );
}

export function isFieldBatchSchemaLink(field: FieldsChainField | SchemaField): boolean {
  return (
    field.fieldType === FieldType.BATCH_LINK &&
    field.requiredLink != null &&
    'batchSchema' in field.requiredLink &&
    field.requiredLink.batchSchema != null
  );
}

// A node in the tree still waiting to have its child fields generated.
interface PendingExpansion {
  tagSchema: TagSchemaSummaryWithFieldsGraphQL;
  parentFieldId: string | null;
  depth: number;
}

// Returns the entity/batch link fields of a schema -- the fields that become nodes
// in the tree. Non-link fields are not selectable as nested lookups.
function getLinkFields(tagSchema: TagSchemaSummaryWithFieldsGraphQL): Array<SchemaField> {
  return (tagSchema.fieldsJson ?? []).filter(
    (field: SchemaField) => isFieldEntitySchemaLink(field) || isFieldBatchSchemaLink(field)
  );
}

/**
 * Builds the flattened nested-field tree for a top-level schema.
 *
 * The tree is generated **breadth-first**: every node at depth N is expanded before
 * any node at depth N+1. This matters when the total-count budget kicks in -- a
 * depth-first walk would let the first link field's subtree consume the entire budget
 * and starve its siblings, producing a lopsided tree. BFS instead grows the tree
 * evenly, so the budget is spent on shallow, broadly-useful levels first.
 *
 * Two limits bound the output:
 *  - MAX_CONFIGURABLE_FIELD_DEPTH caps how deep the tree can go.
 *  - MAX_CONFIGURABLE_FIELD_COUNT caps the total node count, protecting the browser
 *    and the downstream tree picker from pathological (e.g. densely self-referential)
 *    schemas.
 *
 * Expanding a node is atomic in its *immediate children*: a node is expanded only if
 * its whole set of child fields fits in the remaining budget, otherwise it is left as
 * a leaf. We never render a node with a partial set of children (confusing), but
 * sibling nodes at the same depth may differ -- some expanded, some left as leaves --
 * depending on when the budget runs out.
 */
export function getNestedSchemaFieldsForTopLevelSchema({
  tagSchema,
  tagSchemasById,
}: {
  tagSchema: TagSchemaSummaryWithFieldsGraphQL;
  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}): Array<FieldsChainField> {
  const nestedFields: Array<FieldsChainField> = [];
  let remainingFieldCount = MAX_CONFIGURABLE_FIELD_COUNT;

  // `currentLevel` holds the nodes at the depth we're currently expanding. Each entry
  // carries the schema whose link fields become that node's children.
  let currentLevel: Array<PendingExpansion> = [{tagSchema, parentFieldId: null, depth: 0}];

  while (currentLevel.length > 0 && currentLevel[0].depth <= MAX_CONFIGURABLE_FIELD_DEPTH) {
    // Nodes to expand at the next depth. Batch links and not-yet-loaded schemas stay
    // as leaves and are not enqueued here.
    const nextLevel: Array<PendingExpansion> = [];

    for (const node of currentLevel) {
      const childFields = getLinkFields(node.tagSchema);

      // Atomic per node: only expand this node if its entire set of children fits in
      // the remaining budget. Otherwise leave it as a leaf (do not emit a partial set)
      // and move on to its siblings, which may individually still fit.
      if (childFields.length === 0 || childFields.length > remainingFieldCount) {
        continue;
      }
      remainingFieldCount -= childFields.length;

      for (const field of childFields) {
        const uniqueFieldId = generateUniqueFieldId({parentFieldId: node.parentFieldId, id: field.id});
        nestedFields.push({
          ...field,
          uniqueFieldId,
          parentFieldId: node.parentFieldId,
          // The containing schema for this field node (used downstream for schemaId lookups).
          tagSchema: node.tagSchema,
          schemaType: ModelTypeConstants.TAG_SCHEMA,
          requiredLink: {
            tagSchema:
              // eslint-disable-next-line benchlint/no-in-operator
              field.requiredLink && 'tagSchema' in field.requiredLink
                ? (field.requiredLink.tagSchema as FieldsChainField['requiredLink']['tagSchema'])
                : null,
          },
        });

        const linkedSchema = isFieldEntitySchemaLink(field)
          ? tagSchemasById[(field.requiredLink as RequiredLinkTagSchema).tagSchema!.id]
          : undefined;
        if (linkedSchema != null) {
          nextLevel.push({tagSchema: linkedSchema, parentFieldId: uniqueFieldId, depth: node.depth + 1});
        }
      }
    }

    currentLevel = nextLevel;
  }

  return nestedFields;
}

export function getFieldsChainFromLeafToRoot({
  allFields,
  uniqueFieldId,
}: {
  allFields: Array<FieldsChainField>;
  uniqueFieldId: string | null;
}): Array<FieldsChainField> {
  const selectedFieldsChain = [];
  let curFieldId = uniqueFieldId;
  while (curFieldId != null) {
    const field: FieldsChainField | undefined = _.findWhere(allFields, {uniqueFieldId: curFieldId});
    if (field == null) {
      assert(false, `Field with id ${curFieldId} wasn't found.`);
      break;
    }
    selectedFieldsChain.push(field);
    curFieldId = field.parentFieldId;
  }
  return selectedFieldsChain;
}
